// AeroVaid Aviation Academy — central editable content config.
// EDIT PRICING, PHONE, TEXT, SOCIAL LINKS HERE.

export const CONTACT = {
  phoneDisplay: "+91 98671 02681",
  phoneRaw: "919867102681", // used for wa.me and tel:
};

export const WHATSAPP_URL = `https://wa.me/${CONTACT.phoneRaw}`;
export const TEL_URL = `tel:+${CONTACT.phoneRaw}`;

// Public site URL — production canonical domain (non-www preferred).
export const SITE_URL =
  (typeof process !== "undefined" && process.env.REACT_APP_SITE_URL) ||
  "https://aerovaid.in";

// Dedicated SEO landing pages.
export const SEO_LINKS = {
  mumbai: "/dgca-ground-classes-mumbai",
  online: "/online-dgca-ground-classes-india",
};

// Editable pricing — change amount / label here anytime.
export const PRICING = {
  amount: "9,999",
  currency: "₹",
  unit: "/ Subject",
  badge: "Founding Batch",
  tagline: "Premium, personalised DGCA preparation at an accessible founding-batch price.",
  note: "Founding-batch pricing is currently limited and may change as AeroVaid grows.",
  includes: [
    "Live instruction",
    "DGCA-focused preparation",
    "Concept-based teaching",
    "Question practice",
    "Doubt-solving support",
    "Revision guidance",
    "Personalised student support",
  ],
};

export const COURSES = [
  {
    id: "meteorology",
    name: "Meteorology",
    desc: "Understand weather systems, atmospheric behaviour and the phenomena that shape every flight decision.",
  },
  {
    id: "air-navigation",
    name: "Air Navigation",
    desc: "Master charts, calculations and navigation principles the way DGCA examinations expect you to.",
  },
  {
    id: "air-regulations",
    name: "Air Regulations",
    desc: "Build clarity on rules, procedures and the regulatory framework governing Indian and international aviation.",
  },
  {
    id: "technical-general",
    name: "Technical General",
    desc: "Grasp the core principles of aerodynamics, engines, instruments and aircraft systems from the ground up.",
  },
  {
    id: "technical-specific",
    name: "Technical Specific",
    desc: "Type-focused technical preparation that connects systems knowledge to real examination questions.",
  },
  {
    id: "rtr",
    name: "RTR (Radio Telephony)",
    desc: "Prepare for Radio Telephony Restricted with structured practice in procedures and communication.",
  },
];

export const WHY = [
  {
    title: "Personalised Learning",
    desc: "Every student gets individual attention and support.",
  },
  {
    title: "DGCA-Focused Preparation",
    desc: "Study with an approach centred around understanding concepts and preparing effectively for DGCA examinations.",
  },
  {
    title: "Small-Batch & One-to-One Options",
    desc: "Choose personalised attention rather than being lost in a large classroom.",
  },
  {
    title: "Accessible Across India",
    desc: "Online coaching allows aspiring pilots from anywhere in India to learn with AeroVaid.",
  },
  {
    title: "Affordable Launch Pricing",
    desc: "Professional coaching with an accessible founding-batch price.",
  },
];

export const FAQS = [
  {
    q: "Where are AeroVaid classes available?",
    a: "Online classes are available across India. Personalised offline coaching is currently available in Mumbai.",
  },
  {
    q: "Do you have a physical classroom?",
    a: "AeroVaid currently focuses on personalised coaching rather than operating a dedicated classroom. Offline one-to-one sessions can be arranged in Mumbai.",
  },
  {
    q: "How much does a subject cost?",
    a: "The current founding-batch price is ₹9,999 per subject. This is an early-stage introductory price and may change as AeroVaid grows.",
  },
  {
    q: "Can I attend a demo before paying?",
    a: "Yes. Students can request a free demo class before enrolling.",
  },
  {
    q: "Which DGCA subjects do you teach?",
    a: "Meteorology, Air Navigation, Air Regulations, Technical General, Technical Specific and RTR.",
  },
  {
    q: "Can students outside Mumbai join?",
    a: "Yes. Online classes are available across India.",
  },
  {
    q: "Can I get one-to-one coaching?",
    a: "Yes. One-to-one personalised coaching is available in Mumbai, subject to availability and scheduling.",
  },
];

export const SOCIAL = {
  instagram: "https://www.instagram.com/aerovaid?igsi=YnV2d2puNTJzZG5l&utm_source=qr",
  youtube: "https://youtube.com/@aerovaid?si=yRYG_nJtndkr1fFi",
};

export const MEDIA = {
  hero: "https://images.unsplash.com/photo-1516907450399-41d50409e739?crop=entropy&cs=srgb&fm=jpg&q=85&w=2200",
  cockpit: "https://images.unsplash.com/flagged/photo-1579750481098-8b3a62c9b85d?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  founder: "https://customer-assets-agu9un31.emergentagent.net/job_pilot-prep-india/artifacts/puydhc6x_F21D01A1-56A8-4533-9D72-1DFB0360156C.png",
  mumbai: "https://images.pexels.com/photos/7096209/pexels-photo-7096209.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400",
};

export const NAV_LINKS = [
  { label: "Home", href: "#home" },
  { label: "Courses", href: "#courses" },
  { label: "About", href: "#founder" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

// ---------------- Structured data (JSON-LD) ----------------
// Only true, visible information — no invented ratings, reviews or accreditation.
export const ORG_JSONLD = {
  "@context": "https://schema.org",
  "@type": ["EducationalOrganization", "LocalBusiness"],
  "@id": `${SITE_URL}/#organization`,
  name: "AeroVaid Aviation Academy",
  description:
    "DGCA ground coaching for aspiring pilots — personalised classes in Mumbai and live online coaching across India.",
  url: SITE_URL,
  telephone: `+${CONTACT.phoneRaw}`,
  image: MEDIA.founder,
  areaServed: [
    { "@type": "City", name: "Mumbai" },
    { "@type": "Country", name: "India" },
  ],
  address: {
    "@type": "PostalAddress",
    addressLocality: "Mumbai",
    addressRegion: "Maharashtra",
    addressCountry: "IN",
  },
  sameAs: [SOCIAL.instagram, SOCIAL.youtube].filter((u) => u && u !== "#"),
};

export const WEBSITE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  name: "AeroVaid Aviation Academy",
  url: SITE_URL,
  publisher: { "@id": `${SITE_URL}/#organization` },
};

export const faqJsonLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const breadcrumbJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

